/**
 * Mock Data Generator for Dashboard Chart
 *
 * 過去90日間のリアルなサロンデータを生成
 * - 顧客データ（50-100名）
 * - 予約データ（過去90日 × 平均10件/日）
 * - 施術データ（completedステータスの予約に対応）
 */

import type { ExtendedCustomer } from '../types/salonboard';
import type { PhysicalCondition, PaymentMethod } from '@/types/treatment';
import type { Reservation } from '../types/reservation';
import type { TreatmentRecord } from '../types/treatment';

// 日本人名前データ
const LAST_NAMES = [
  '佐藤',
  '鈴木',
  '高橋',
  '田中',
  '伊藤',
  '渡辺',
  '山本',
  '中村',
  '小林',
  '加藤',
  '吉田',
  '山田',
  '佐々木',
  '山口',
  '松本',
  '井上',
  '木村',
  '林',
  '清水',
  '山崎',
];

const FIRST_NAMES_FEMALE = [
  '美咲',
  '花子',
  '優子',
  '真理',
  '恵子',
  'さくら',
  'あい',
  'ゆい',
  '陽菜',
  '結衣',
  '愛',
  '美紀',
  '由美',
  '麻衣',
  '彩',
  '奈々',
  '千尋',
  '舞',
  '瑞希',
  '七海',
];

const FIRST_NAMES_MALE = [
  '太郎',
  '健太',
  '大輔',
  '翔',
  '拓也',
  '悠',
  '誠',
  '翔太',
  '健',
  '大樹',
];

// サービスメニュー
const SERVICES = [
  { name: 'ドライヘッドスパ60分', price: 8500, duration: 60 },
  { name: 'アロマヘッドスパ45分', price: 6500, duration: 45 },
  { name: 'プレミアムヘッドスパ90分', price: 12000, duration: 90 },
  { name: 'ベーシックヘッドスパ30分', price: 4500, duration: 30 },
  { name: 'リラクゼーションヘッドスパ75分', price: 10000, duration: 75 },
];

// スタッフ名
const STAFF_NAMES = ['田中美香', '佐藤恵子', '山本さくら', '鈴木優', '高橋愛'];

/**
 * ランダムな整数を生成
 */
function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * ランダムな日本人名を生成
 */
function generateJapaneseName(): {
  lastName: string;
  firstName: string;
  fullName: string;
} {
  const lastName = LAST_NAMES[randomInt(0, LAST_NAMES.length - 1)];
  const firstNamePool =
    Math.random() > 0.8 ? FIRST_NAMES_MALE : FIRST_NAMES_FEMALE;
  const firstName = firstNamePool[randomInt(0, firstNamePool.length - 1)];
  return {
    lastName,
    firstName,
    fullName: `${lastName} ${firstName}`,
  };
}

/**
 * 電話番号を生成
 */
function generatePhone(): string {
  const prefix = ['090', '080', '070'][randomInt(0, 2)];
  const middle = String(randomInt(1000, 9999));
  const last = String(randomInt(1000, 9999));
  return `${prefix}-${middle}-${last}`;
}

/**
 * メールアドレスを生成
 */
function generateEmail(name: { lastName: string; firstName: string }): string {
  const domains = [
    'gmail.com',
    'yahoo.co.jp',
    'docomo.ne.jp',
    'ezweb.ne.jp',
    'softbank.ne.jp',
  ];
  const domain = domains[randomInt(0, domains.length - 1)];
  const romanji = `${name.firstName.toLowerCase()}.${name.lastName.toLowerCase()}`;
  return `${romanji}${randomInt(100, 999)}@${domain}`;
}

/**
 * 日付をYYYY-MM-DD形式にフォーマット
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 顧客データを生成
 */
export function generateCustomers(count: number = 80): ExtendedCustomer[] {
  const customers: ExtendedCustomer[] = [];
  const now = new Date();

  // 新規顧客: 20%、リピーター: 80%
  const newCustomerCount = Math.floor(count * 0.2);

  for (let i = 0; i < count; i++) {
    const name = generateJapaneseName();
    const isNew = i < newCustomerCount;
    const totalVisits = isNew ? 1 : randomInt(2, 20);
    const avgSpending = randomInt(6000, 12000);
    const totalSpent = totalVisits * avgSpending;

    // ランクを来店回数から算出
    let rank: 'bronze' | 'silver' | 'gold' | 'platinum' = 'bronze';
    if (totalVisits >= 20) rank = 'platinum';
    else if (totalVisits >= 10) rank = 'gold';
    else if (totalVisits >= 5) rank = 'silver';

    // 最終来店日（過去90日以内）
    const daysAgo = randomInt(0, 90);
    const lastVisit = new Date(now);
    lastVisit.setDate(lastVisit.getDate() - daysAgo);

    // 初回来店日
    const firstVisitDaysAgo = daysAgo + (totalVisits - 1) * randomInt(7, 30);
    const firstVisitDate = new Date(now);
    firstVisitDate.setDate(firstVisitDate.getDate() - firstVisitDaysAgo);

    customers.push({
      id: String(i + 1),
      customerCode: `C${String(i + 1).padStart(7, '0')}`,
      firstName: name.firstName,
      lastName: name.lastName,
      nameKana: `${name.lastName}${name.firstName}`,
      email: generateEmail(name),
      phoneNumber: generatePhone(),
      birthday: '',
      occupation: '',
      address: '',
      preferences: '',
      lastVisit: formatDate(lastVisit),
      totalVisits,
      totalSpent,
      status: 'ACTIVE',
      rank,
      tags: isNew ? ['新規'] : [],
      preferredStaff: STAFF_NAMES[randomInt(0, STAFF_NAMES.length - 1)],
      createdAt: formatDate(firstVisitDate),
      updatedAt: formatDate(lastVisit),
      gender: Math.random() > 0.8 ? 'male' : 'female',
      firstVisitDate: formatDate(firstVisitDate),
      isVip: rank === 'platinum',
      mergeHistory: {
        mergedFrom: [],
      },
      messageSettings: {
        email: true,
        myPage: false,
        sms: false,
      },
      deliveryHistory: [],
      customFields: {},
    });
  }

  return customers;
}

/**
 * 予約データを生成（過去90日間）
 */
export function generateReservations(
  customers: ExtendedCustomer[],
  daysBack: number = 90,
  avgPerDay: number = 10
): Reservation[] {
  const reservations: Reservation[] = [];
  const now = new Date();
  let reservationId = 1;

  for (let day = 0; day < daysBack; day++) {
    const date = new Date(now);
    date.setDate(date.getDate() - day);

    // 土日は1.5倍、平日は通常
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const reservationsPerDay = Math.floor(avgPerDay * (isWeekend ? 1.5 : 1));

    // 1日の予約を生成
    for (let i = 0; i < reservationsPerDay; i++) {
      const customer = customers[randomInt(0, customers.length - 1)];
      const service = SERVICES[randomInt(0, SERVICES.length - 1)];
      const staff = STAFF_NAMES[randomInt(0, STAFF_NAMES.length - 1)];

      // 営業時間 10:00-20:00、1時間刻み
      const hour = randomInt(10, 19);
      const startTime = `${String(hour).padStart(2, '0')}:00`;
      const endHour = hour + Math.floor(service.duration / 60);
      const endTime = `${String(endHour).padStart(2, '0')}:00`;

      // ステータス: 日付を考慮した現実的な分布
      // 過去: ほぼ完了済み、未来/本日: ほぼ確定済み
      let status: 'COMPLETED' | 'CONFIRMED' | 'CANCELLED';
      const rand = Math.random();

      if (day > 0) {
        // 過去の日付: 95% completed, 5% cancelled
        if (rand < 0.95) status = 'COMPLETED';
        else status = 'CANCELLED';
      } else {
        // 本日/未来の日付: 90% confirmed, 5% completed, 5% cancelled
        if (rand < 0.9) status = 'CONFIRMED';
        else if (rand < 0.95) status = 'COMPLETED';
        else status = 'CANCELLED';
      }

      reservations.push({
        id: String(reservationId++),
        customerId: customer.id,
        customerName: `${customer.lastName} ${customer.firstName}`,
        customerPhone: customer.phoneNumber,
        customerEmail: customer.email || '',
        staffId: String(randomInt(1, STAFF_NAMES.length)),
        staffName: staff,
        serviceId: String(randomInt(1, SERVICES.length)),
        serviceName: service.name,
        reservationDate: date,
        startTime,
        endTime,
        duration: service.duration,
        status,
        customerRequest: '',
        createdAt: date,
        updatedAt: date,
      });
    }
  }

  return reservations;
}

/**
 * 施術データを生成（completedステータスの予約のみ）
 */
export function generateTreatments(
  reservations: Reservation[]
): TreatmentRecord[] {
  const treatments: TreatmentRecord[] = [];
  let treatmentId = 1;

  const completedReservations = reservations.filter(
    (r) => r.status === 'COMPLETED'
  );

  for (const reservation of completedReservations) {
    const service =
      SERVICES.find((s) => s.name === reservation.serviceName) || SERVICES[0];

    // 売上: 基本料金 + ランダムなオプション（0-3000円）
    const basePrice = service.price;
    const optionPrice = randomInt(0, 3) * 1000; // 0円, 1000円, 2000円, 3000円
    const totalAmount = basePrice + optionPrice;

    treatments.push({
      id: String(treatmentId++),
      customerId: reservation.customerId,
      customerName: reservation.customerName,
      treatmentDate:
        reservation.reservationDate instanceof Date
          ? reservation.reservationDate.toISOString()
          : String(reservation.reservationDate),
      createdAt:
        reservation.reservationDate instanceof Date
          ? reservation.reservationDate.toISOString()
          : String(reservation.reservationDate),
      updatedAt:
        reservation.reservationDate instanceof Date
          ? reservation.reservationDate.toISOString()
          : String(reservation.reservationDate),
      status: 'completed',
      // Issue #3790 (#3780): mock 生成。未割当 (NULL) は空文字で代替 (dev データのみ)。
      staffId: reservation.staffId ?? '',
      staffName: reservation.staffName,
      reservationId: reservation.id,

      // 身体状態（簡易版）
      physicalCondition: '普通' as PhysicalCondition,
      symptoms: [],

      // 施術詳細
      treatmentMenu: reservation.serviceName,
      treatmentDuration: reservation.duration,
      techniques: [],
      focusAreas: [],

      // カウンセリング情報
      lifestyle: {},
      requests: '',
      concerns: '',

      // 統合メモ
      notes: {
        before: '',
        during: '',
        after: '',
        conversation: '',
      },

      // 施術結果
      improvements: [],
      feedback: '',

      // 会計情報
      treatmentAmount: basePrice,
      productAmount: optionPrice,
      totalAmount,
      paymentMethod: 'cash' as PaymentMethod,

      // メタ情報
      isFirstVisit: false,
      visitNumber: 1,
      followUpRequired: false,
      tenantId: 'demo',
    });
  }

  return treatments;
}

/**
 * すべてのモックデータを生成
 */
export function generateAllMockData() {
  console.log('🎲 モックデータ生成開始...');

  const customers = generateCustomers(80);
  console.log(`✅ 顧客データ生成完了: ${customers.length}件`);

  const reservations = generateReservations(customers, 90, 10);
  console.log(`✅ 予約データ生成完了: ${reservations.length}件`);

  const treatments = generateTreatments(reservations);
  console.log(`✅ 施術データ生成完了: ${treatments.length}件`);

  // 統計情報
  const newCustomers = customers.filter((c) => c.totalVisits === 1).length;
  const repeatCustomers = customers.filter((c) => c.totalVisits >= 2).length;
  const completedReservations = reservations.filter(
    (r) => r.status === 'COMPLETED'
  ).length;
  const totalRevenue = treatments.reduce((sum, t) => sum + t.totalAmount, 0);

  console.log('\n📊 データ統計:');
  console.log(
    `  新規顧客: ${newCustomers}件 (${Math.round((newCustomers / customers.length) * 100)}%)`
  );
  console.log(
    `  リピーター: ${repeatCustomers}件 (${Math.round((repeatCustomers / customers.length) * 100)}%)`
  );
  console.log(`  完了予約: ${completedReservations}件`);
  console.log(`  総売上: ¥${totalRevenue.toLocaleString()}`);
  console.log(
    `  平均客単価: ¥${Math.round(totalRevenue / completedReservations).toLocaleString()}`
  );

  return {
    customers,
    reservations,
    treatments,
    stats: {
      newCustomers,
      repeatCustomers,
      completedReservations,
      totalRevenue,
      avgSpending: Math.round(totalRevenue / completedReservations),
    },
  };
}
